import { useState, useCallback } from 'react';

import { snackbar } from '@occc-app/design-system';
import { formatDateTime, DateTimeFormat } from '@occc-app/lib/date';

import { useScanDataSubmitWeeklyFile } from '../../../hooks/use-scan-data-submit-weekly-file.mutation.hook';

export type UseScanDataWeeklyFileResubmitReturn = {
  isResubmitDialogOpen: boolean;
  currentSubmissionDate: string | null;
  openResubmitDialog: (submissionDate: string) => void;
  closeResubmitDialog: () => void;
  handleResubmitConfirm: () => Promise<void>;
  isSubmitting: boolean;
};

export const useScanDataWeeklyFileResubmit = (): UseScanDataWeeklyFileResubmitReturn => {
  const [isResubmitDialogOpen, setIsResubmitDialogOpen] = useState(false);
  const [currentSubmissionDate, setCurrentSubmissionDate] = useState<string | null>(null);
  const [submitWeeklyFile, { loading: isSubmitting }] = useScanDataSubmitWeeklyFile();

  const openResubmitDialog = useCallback((submissionDate: string) => {
    setCurrentSubmissionDate(submissionDate);
    setIsResubmitDialogOpen(true);
  }, []);

  const closeResubmitDialog = useCallback(() => {
    setIsResubmitDialogOpen(false);
    setCurrentSubmissionDate(null);
  }, []);

  const onCompleted = useCallback(() => {
    snackbar.success('Weekly file resubmitted successfully.');
    setIsResubmitDialogOpen(false);
    setCurrentSubmissionDate(null);
  }, []);

  const onError = useCallback(() => {
    snackbar.error('Failed to resubmit weekly file. Please try again.');
    setIsResubmitDialogOpen(false);
    setCurrentSubmissionDate(null);
  }, []);

  const handleResubmitConfirm = useCallback(async () => {
    if (!currentSubmissionDate) {
      return;
    }

    const formattedDate = formatDateTime(
      new Date(currentSubmissionDate),
      DateTimeFormat.DateDashed,
    );

    await submitWeeklyFile({
      variables: {
        input: {
          submissionDate: formattedDate,
        },
      },
      onCompleted,
      onError,
    });
    setIsResubmitDialogOpen(false);
  }, [submitWeeklyFile, currentSubmissionDate, onCompleted, onError]);

  return {
    isResubmitDialogOpen,
    currentSubmissionDate,
    openResubmitDialog,
    closeResubmitDialog,
    handleResubmitConfirm,
    isSubmitting,
  };
};
